import { Card, CardContent, CardHeader, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { tokens } from '../../theme';
import { IPrescription } from "../../types";

type calculationMode = 'countPerDay' | 'amountPerDay';

interface DashboardBarChartProps {
  prescriptions: IPrescription[];
  calculation: calculationMode;
  startDate: Date;
  endDate: Date;
}

interface TotalByDay {
  date: number;
  total: number;
}

const currencyFormat = Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const getHeaderTitle = (prescriptions: IPrescription[], calculation: calculationMode): string => {
  switch (calculation) {
    case 'amountPerDay': {
      const totalAmount = prescriptions.reduce((aggregated, item) => aggregated += item.price, 0.00);
      const total = currencyFormat.format(totalAmount);
      return `Prescriptions Total: ${total} CAD`;
    }
    case 'countPerDay': {
      const total = Intl.NumberFormat('en-US').format(prescriptions.length);
      return `Prescriptions Count: ${total} Rx(s)`;
    }
    default:
      return '';
  }
};

const getPrescriptionsPerDay = (dashboardBarChartProps: DashboardBarChartProps): TotalByDay[] => {
  const { prescriptions, calculation, startDate, endDate } = dashboardBarChartProps;
  const prescriptionsPerDay = prescriptions.reduce((aggregated, item) => {
    const day = format(new Date(item.rxDate), 'yyyy-MM-dd');
    if (!aggregated[day]) {
      aggregated[day] = 0;
    }
    
    aggregated[day] += calculation === 'countPerDay' ? 1 : item.price;
    return aggregated;
  }, {} as { [key: string]: number });

  const arrayOfDays: string[] = [];
  const startDateClone = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const endDateClone = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  while (startDateClone <= endDateClone) {
    arrayOfDays.push(format(startDateClone, 'yyyy-MM-dd'));
    startDateClone.setDate(startDateClone.getDate() + 1);
  }

  return arrayOfDays.map((item, index) => ({
    date: index + 1,
    total: prescriptionsPerDay[item] || 0,
  }));
};

const DashboardBarChart = (dashboardBarChartProps: DashboardBarChartProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { prescriptions, calculation, startDate } = dashboardBarChartProps;

  if (!prescriptions) {
    return null;
  }

  const yAxisTickFormatter = (total: number): string => Intl.NumberFormat('en-US').format(total);
  const xAxisTickFormatter = (date: number): string => { 
    const startDateClone = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    startDateClone.setDate(startDateClone.getDate() + (date - 1));
    return format(startDateClone, 'MM/dd');
  };

  const toolTipLabelFormatter = (date: number): string => { 
    const startDateClone = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    startDateClone.setDate(startDateClone.getDate() + (date - 1));
   return format(startDateClone, 'MM/dd/yyyy');
  };

  const data = getPrescriptionsPerDay(dashboardBarChartProps);
  
  return (
    <Card sx={{
      backgroundColor: colors.primary[400] 
    }}>
      <CardHeader 
        title={
          <Typography 
            variant="h5" 
            fontWeight="bold" 
            color={colors.greenAccent[500]}
          >
            {getHeaderTitle(prescriptions, calculation)}
          </Typography>
        } 
      />
      
      <CardContent>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={data}>
              <XAxis 
                dataKey="date" 
                name="Date" 
                tickFormatter={xAxisTickFormatter} 
                interval={Math.ceil(data.length / 15)} 
              />
              <YAxis 
                dataKey="total" 
                name={calculation === 'amountPerDay' ? 'Total Price' : 'Total Count'} 
                unit={calculation === 'amountPerDay' ? ' $' : ' Rx(s)'}
                tickFormatter={yAxisTickFormatter}
              />
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                formatter={(value: any) => calculation === 'amountPerDay' ? `${currencyFormat.format(value)} CAD` : `${Intl.NumberFormat('en-US').format(value)} Rx(s)`}
                labelFormatter={(label: any) => toolTipLabelFormatter(label)} 
                labelStyle={{ color: colors.primary[500] }}
              />
              <Bar dataKey="total" fill={colors.blueAccent[700]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardBarChart;